import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// ---------------------------------------------------------------------------
// RecommendationsList — deterministic CTAs from analyzeCaseload().recommendations.
//
// Each recommendation carries an internal route + state so the CTA
// actually takes the teacher somewhere useful. Priority sorts high →
// medium → low and colors the left border accordingly.
// ---------------------------------------------------------------------------

const PRIORITY_STYLES = {
  high: { border: 'border-l-red-500', label: 'High', badge: 'bg-red-100 text-red-700' },
  medium: { border: 'border-l-amber-500', label: 'Attention', badge: 'bg-amber-100 text-amber-700' },
  low: { border: 'border-l-emerald-500', label: 'Opportunity', badge: 'bg-emerald-100 text-emerald-700' },
};

const RecommendationsList = ({ recommendations = [] }) => {
  const navigate = useNavigate();

  if (!recommendations || recommendations.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
        <Icon name="CheckCircle2" size={20} className="text-emerald-500 mx-auto mb-2" />
        <p className="text-sm text-slate-500">
          No open recommendations. Your caseload's looking clean — well done.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {recommendations.map((r) => {
        const priority = PRIORITY_STYLES[r.priority] || PRIORITY_STYLES.medium;
        return (
          <div
            key={r.id}
            className={`bg-white border border-slate-200 rounded-lg border-l-4 ${priority.border} p-4`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 text-slate-500">
                <Icon name={r.icon || 'Lightbulb'} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm text-slate-900">{r.headline}</h4>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${priority.badge}`}
                  >
                    {priority.label}
                  </span>
                </div>
                <p className="text-sm text-slate-700">{r.detail}</p>
                {r.ctaTo && (
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(r.ctaTo, { state: r.ctaState })}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      {r.ctaLabel || 'Open'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RecommendationsList;
